import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import stateData from '../data/stateData';

const { FiMessageCircle, FiX, FiSend, FiUser, FiBot, FiExternalLink, FiAlertCircle, FiMapPin, FiScale, FiDollarSign, FiFileText, FiInfo, FiUsers } = FiIcons;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: "Hello! I'm Joe, your dedicated dad support resource agent. I'm here to help you navigate family law matters and connect you with the resources and information you need to protect your parental rights.",
      timestamp: new Date()
    },
    {
      id: 2,
      type: 'bot',
      content: "To get started, please tell me which state you're located in so I can provide you with the most relevant information and official resources. Remember, while I provide helpful guidance and resources, I'm not a legal expert - always consult with a qualified family law attorney for specific legal advice.",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const states = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'Washington DC'
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = (content, type = 'user') => {
    const newMessage = {
      id: Date.now(),
      type,
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const simulateTyping = async (delay = 1500) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, delay));
    setIsTyping(false);
  };

  const detectStateInMessage = (message) => {
    const lowerMessage = message.toLowerCase();
    return states.find(state => 
      lowerMessage.includes(state.toLowerCase()) || 
      lowerMessage.includes(state.toLowerCase().replace(' ', ''))
    );
  };

  const getStateSlug = (stateName) => {
    if (stateName.toLowerCase() === 'washington dc') {
      return 'washington-dc';
    }
    return stateName.toLowerCase().replace(/\s+/g, '-');
  };

  const generateStateResponse = async (stateName) => {
    await simulateTyping(2000);
    
    const stateSlug = getStateSlug(stateName);
    const stateInfo = stateData[stateSlug];
    
    let response = `# Dad Support Resources for ${stateName}\n\n`;
    
    if (stateInfo) {
      response += `## Key Information for Fathers in ${stateName}\n\n`;
      response += `**Important Family Law Points:**\n`;
      stateInfo.overview.keyPoints.slice(0, 3).forEach((point, index) => {
        response += `${index + 1}. ${point}\n`;
      });
      
      response += `\n**Understanding ${stateName}'s System:**\n`;
      response += `• **Physical Custody:** ${stateInfo.custodyTypes.physicalCustody}\n`;
      response += `• **Legal Custody:** ${stateInfo.custodyTypes.legalCustody}\n`;
      response += `• **Joint Custody:** ${stateInfo.custodyTypes.jointCustody}\n\n`;
      
      response += `**Child Support Overview:**\n`;
      response += `${stateInfo.childSupport.guidelines}\n\n`;
    } else {
      response += `I have general dad support information for ${stateName}. Let me share the essential framework:\n\n`;
      response += `**Core Principles for Fathers:**\n`;
      response += `1. **Best Interests of the Child:** Courts prioritize what serves your child's physical, emotional, and developmental needs\n`;
      response += `2. **Parental Rights:** You have fundamental rights to maintain a relationship with your children\n`;
      response += `3. **Income Shares Model:** Most states calculate child support based on both parents' combined income\n\n`;
    }
    
    response += `## Official Resources & Next Steps\n\n`;
    response += `**State Resources:**\n`;
    
    // Add state-specific official links
    const stateLinks = getOfficialStateLinks(stateName);
    stateLinks.forEach(link => {
      response += `• [${link.name}](${link.url}) - ${link.description}\n`;
    });
    
    response += `\n## Your Action Plan\n\n`;
    response += `**1. Get Your Documents Ready:**\n`;
    response += `• Visit your state's court website for official forms\n`;
    response += `• Look for "Family Court Forms" or "Self-Help" sections\n`;
    response += `• Download custody, support, and paternity forms as needed\n\n`;
    
    response += `**2. Filing Your Case:**\n`;
    response += `• **In Person:** Visit your local family court clerk's office\n`;
    response += `• **By Mail:** Check if your court accepts mailed filings\n`;
    response += `• **Online:** Many courts now offer e-filing systems\n\n`;
    
    response += `**3. Managing Your Case:**\n`;
    response += `• Create an account on your state's child support portal\n`;
    response += `• Keep detailed records of all payments and communications\n`;
    response += `• Document your time with your children\n\n`;
    
    response += `**4. Protecting Your Rights:**\n`;
    response += `• File modification requests when circumstances change\n`;
    response += `• Contact enforcement agencies if needed\n`;
    response += `• Always follow court orders precisely\n\n`;
    
    response += `## Understanding the System\n\n`;
    response += `**"Best Interests of the Child" means:**\n`;
    response += `• Your child's physical and emotional health comes first\n`;
    response += `• Courts want stability in your child's living situation\n`;
    response += `• Your relationship quality with your child matters\n`;
    response += `• Age-appropriate input from your child may be considered\n\n`;
    
    response += `**Types of Custody Explained:**\n`;
    response += `• **Physical Custody:** Where your child lives day-to-day\n`;
    response += `• **Legal Custody:** Your right to make major decisions (education, healthcare, religion)\n\n`;
    
    response += `**Modern "Time-Sharing":** Many states now use this term instead of "visitation" to emphasize both parents' ongoing roles\n\n`;
    
    response += `**Support Enforcement:** Government services help collect unpaid child support through wage garnishment, asset seizure, and other legal methods\n\n`;
    
    response += `## Important Reminders\n\n`;
    response += `**I'm here as your dad support resource agent to help you navigate this process, but I'm not a legal expert.** For case-specific legal advice, please consult:\n`;
    response += `• A licensed family law attorney in your state\n`;
    response += `• Legal aid services for low-income individuals\n`;
    response += `• Your local family court self-help center\n\n`;
    
    response += `**Remember:** You have rights as a father, and there are resources available to help you protect them. Would you like me to provide more specific information about custody arrangements, child support calculations, or court procedures in ${stateName}?`;
    
    addMessage(response, 'bot');
    setSelectedState(stateName);
  };

  const getOfficialStateLinks = (stateName) => {
    const stateSlug = getStateSlug(stateName);
    const stateInfo = stateData[stateSlug];
    
    if (stateInfo && stateInfo.resources) {
      const links = [];
      
      // Add court resources
      if (stateInfo.resources.courts) {
        stateInfo.resources.courts.slice(0, 3).forEach(resource => {
          if (typeof resource === 'object' && resource.url) {
            links.push({
              name: resource.name,
              url: resource.url,
              description: resource.description
            });
          }
        });
      }
      
      // Add support resources
      if (stateInfo.resources.support) {
        stateInfo.resources.support.slice(0, 2).forEach(resource => {
          if (typeof resource === 'object' && resource.url) {
            links.push({
              name: resource.name,
              url: resource.url,
              description: resource.description
            });
          }
        });
      }
      
      return links;
    }
    
    // Default links for states without detailed data
    return [
      {
        name: `${stateName} Judicial Branch`,
        url: `https://www.google.com/search?q=${encodeURIComponent(stateName + ' judicial branch family court')}`,
        description: 'Official state court system website'
      },
      {
        name: `${stateName} Child Support Services`,
        url: `https://www.google.com/search?q=${encodeURIComponent(stateName + ' child support enforcement')}`,
        description: 'State child support enforcement agency'
      },
      {
        name: `${stateName} Legal Aid`,
        url: `https://www.google.com/search?q=${encodeURIComponent(stateName + ' legal aid family law')}`,
        description: 'Free legal assistance for qualifying individuals'
      }
    ];
  };

  const generateTopicResponse = async (topic) => {
    await simulateTyping(1500);
    
    let response = '';
    
    if (topic.includes('custody') || topic.includes('parenting')) {
      response = `## Dad's Guide to Child Custody\n\n`;
      response += `**Types of Custody Arrangements:**\n\n`;
      response += `**1. Physical Custody**\n`;
      response += `• Determines where your child lives on a day-to-day basis\n`;
      response += `• Can be sole (with one parent) or joint (shared between parents)\n`;
      response += `• Joint physical custody means significant time with both parents\n\n`;
      
      response += `**2. Legal Custody**\n`;
      response += `• Your authority to make major decisions about your child's life\n`;
      response += `• Includes education, healthcare, religious upbringing\n`;
      response += `• Usually awarded jointly unless there are safety concerns\n\n`;
      
      response += `**Building Your Custody Case:**\n`;
      response += `• Document your daily involvement in your child's life\n`;
      response += `• Keep records of school events, medical appointments you attend\n`;
      response += `• Show your ability to provide a stable, loving environment\n`;
      response += `• Demonstrate your willingness to co-parent effectively\n\n`;
      
      if (selectedState) {
        response += `For ${selectedState}-specific custody laws and forms, I recommend checking your state's judicial website.\n\n`;
      }
      
      response += `**Your Next Steps:**\n`;
      response += `1. Research your state's custody factors and guidelines\n`;
      response += `2. Document your involvement in your child's life\n`;
      response += `3. Consider mediation before court proceedings\n`;
      response += `4. Consult with a family law attorney\n\n`;
      
    } else if (topic.includes('support') || topic.includes('money') || topic.includes('payment')) {
      response = `## Dad's Guide to Child Support\n\n`;
      response += `**How Child Support is Calculated:**\n\n`;
      response += `Most states use the **Income Shares Model**:\n`;
      response += `1. Calculate both parents' combined gross income\n`;
      response += `2. Determine support amount from state guidelines\n`;
      response += `3. Allocate proportionally based on each parent's income\n`;
      response += `4. Add costs for health insurance and childcare\n`;
      response += `5. Adjust for significant parenting time (if applicable)\n\n`;
      
      response += `**Income That Counts:**\n`;
      response += `• Wages, salary, bonuses, and commissions\n`;
      response += `• Self-employment income\n`;
      response += `• Investment income and rental property\n`;
      response += `• Social Security and disability benefits\n`;
      response += `• Unemployment compensation\n\n`;
      
      response += `**Additional Expenses Often Added:**\n`;
      response += `• Health insurance premiums for your child\n`;
      response += `• Childcare costs for work or education\n`;
      response += `• Extraordinary medical expenses\n`;
      response += `• Educational expenses (sometimes)\n\n`;
      
      response += `**If You're Behind on Payments:**\n`;
      response += `• Contact child support enforcement immediately\n`;
      response += `• File for modification if your income changed\n`;
      response += `• Never ignore court orders - this can lead to serious consequences\n`;
      response += `• Know that enforcement methods include wage garnishment, asset seizure, and license suspension\n\n`;
      
      if (selectedState) {
        response += `${selectedState} may have specific guidelines and online calculators available through the state's child support agency.\n\n`;
      }
      
    } else if (topic.includes('court') || topic.includes('file') || topic.includes('form')) {
      response = `## Navigating Family Court as a Dad\n\n`;
      response += `**Types of Cases You Might Face:**\n`;
      response += `• Divorce and legal separation\n`;
      response += `• Child custody and parenting time\n`;
      response += `• Child support establishment and modification\n`;
      response += `• Paternity determination\n`;
      response += `• Domestic violence protection orders\n`;
      response += `• Adoption proceedings\n\n`;
      
      response += `**Your Court Filing Process:**\n\n`;
      response += `**1. Prepare Your Documents:**\n`;
      response += `• Complete all required forms accurately and completely\n`;
      response += `• Gather supporting documentation (financial records, etc.)\n`;
      response += `• Make copies for your records and the other party\n\n`;
      
      response += `**2. File with the Court:**\n`;
      response += `• Submit forms to the family court clerk\n`;
      response += `• Pay required filing fees (fee waivers may be available)\n`;
      response += `• Obtain your case number and court dates\n\n`;
      
      response += `**3. Serve the Other Party:**\n`;
      response += `• Legal documents must be properly served\n`;
      response += `• Use sheriff's department or process server\n`;
      response += `• File proof of service with the court\n\n`;
      
      response += `**Resources Available to You:**\n`;
      response += `• Most courts have self-help centers for dads representing themselves\n`;
      response += `• Free form assistance and basic legal information\n`;
      response += `• Mediation services often available and encouraged\n`;
      response += `• Online resources and video tutorials\n\n`;
      
      if (selectedState) {
        response += `Check ${selectedState}'s judicial website for state-specific forms and local court rules.\n\n`;
      }
    }
    
    response += `Need more specific information? Please let me know what aspect you'd like me to explain further! Remember, I'm here as your support resource agent, but for legal advice specific to your case, always consult with a qualified attorney.`;
    
    addMessage(response, 'bot');
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    const userMessage = inputMessage.trim();
    addMessage(userMessage, 'user');
    setInputMessage('');
    
    // Detect if user mentioned a state
    const detectedState = detectStateInMessage(userMessage);
    if (detectedState && !selectedState) {
      await generateStateResponse(detectedState);
      return;
    }
    
    // Handle topic-specific questions
    const lowerMessage = userMessage.toLowerCase();
    if (lowerMessage.includes('custody') || lowerMessage.includes('parenting') || lowerMessage.includes('visitation')) {
      await generateTopicResponse('custody');
    } else if (lowerMessage.includes('support') || lowerMessage.includes('money') || lowerMessage.includes('payment') || lowerMessage.includes('calculate')) {
      await generateTopicResponse('support');
    } else if (lowerMessage.includes('court') || lowerMessage.includes('file') || lowerMessage.includes('form') || lowerMessage.includes('procedure')) {
      await generateTopicResponse('court');
    } else if (states.some(state => lowerMessage.includes(state.toLowerCase()))) {
      const state = states.find(s => lowerMessage.includes(s.toLowerCase()));
      await generateStateResponse(state);
    } else {
      // General response
      await simulateTyping();
      
      let response = `I'm here to help you with dad support resources including:\n\n`;
      response += `**Child Custody & Parenting Plans**\n`;
      response += `**Child Support Calculations & Enforcement**\n`;
      response += `**Family Court Procedures & Forms**\n`;
      response += `**State-Specific Legal Requirements**\n\n`;
      
      if (!selectedState) {
        response += `To provide the most accurate information, please tell me which state you're in.\n\n`;
      }
      
      response += `You can ask me questions like:\n`;
      response += `• "How is child support calculated?"\n`;
      response += `• "What types of custody are available?"\n`;
      response += `• "How do I file for custody?"\n`;
      response += `• "What are my rights as a father?"\n\n`;
      
      response += `Remember, I'm your dad support resource agent - I provide helpful information and resources, but I'm not a legal expert. For specific legal advice about your case, always consult with a qualified family law attorney.\n\n`;
      
      response += `What specific topic would you like to know more about?`;
      
      addMessage(response, 'bot');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const MessageContent = ({ content }) => {
    // Simple markdown-like rendering
    const formatContent = (text) => {
      return text
        .split('\n')
        .map((line, index) => {
          if (line.startsWith('# ')) {
            return <h3 key={index} className="text-lg font-bold text-gray-900 mb-2">{line.slice(2)}</h3>;
          }
          if (line.startsWith('## ')) {
            // Remove emojis from headers using a safer approach
            const cleanLine = line.slice(2).replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim();
            return (
              <h4 key={index} className="text-md font-semibold text-gray-800 mb-2 mt-4 flex items-center">
                {line.includes('Key') && <SafeIcon icon={FiFileText} className="mr-2 text-primary-600" />}
                {line.includes('Official') && <SafeIcon icon={FiScale} className="mr-2 text-primary-600" />}
                {line.includes('Child Support') && <SafeIcon icon={FiDollarSign} className="mr-2 text-primary-600" />}
                {line.includes('Action Plan') && <SafeIcon icon={FiFileText} className="mr-2 text-secondary-600" />}
                {line.includes('Understanding') && <SafeIcon icon={FiInfo} className="mr-2 text-accent-600" />}
                {line.includes('Important') && <SafeIcon icon={FiAlertCircle} className="mr-2 text-red-600" />}
                {line.includes('Dad') && <SafeIcon icon={FiUsers} className="mr-2 text-primary-600" />}
                {line.includes('Court') && <SafeIcon icon={FiScale} className="mr-2 text-primary-600" />}
                {cleanLine}
              </h4>
            );
          }
          if (line.startsWith('**') && line.endsWith('**')) {
            return <p key={index} className="font-semibold text-gray-800 mb-1">{line.slice(2, -2)}</p>;
          }
          if (line.startsWith('• ')) {
            // Handle links in bullet points
            const linkMatch = line.match(/\[([^\]]+)\]\(([^)]+)\)/);
            if (linkMatch) {
              const [fullMatch, linkText, url] = linkMatch;
              const beforeLink = line.slice(2, line.indexOf(fullMatch));
              const afterLink = line.slice(line.indexOf(fullMatch) + fullMatch.length);
              return (
                <p key={index} className="text-gray-700 mb-1 ml-4">
                  • {beforeLink}
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 underline inline-flex items-center"
                  >
                    {linkText}
                    <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
                  </a>
                  {afterLink}
                </p>
              );
            }
            return <p key={index} className="text-gray-700 mb-1 ml-4">{line}</p>;
          }
          if (line.match(/^\d+\./)) {
            return <p key={index} className="text-gray-700 mb-1 ml-4">{line}</p>;
          }
          if (line.trim() === '') {
            return <br key={index} />;
          }
          
          // Handle links in regular paragraphs
          const linkMatch = line.match(/\[([^\]]+)\]\(([^)]+)\)/);
          if (linkMatch) {
            const [fullMatch, linkText, url] = linkMatch;
            const beforeLink = line.slice(0, line.indexOf(fullMatch));
            const afterLink = line.slice(line.indexOf(fullMatch) + fullMatch.length);
            return (
              <p key={index} className="text-gray-700 mb-2">
                {beforeLink}
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline inline-flex items-center"
                >
                  {linkText}
                  <SafeIcon icon={FiExternalLink} className="ml-1 text-xs" />
                </a>
                {afterLink}
              </p>
            );
          }
          
          return <p key={index} className="text-gray-700 mb-2">{line}</p>;
        });
    };
    
    return <div className="space-y-1">{formatContent(content)}</div>;
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-4 shadow-lg transition-all duration-300 ${
          isOpen ? 'scale-0' : 'scale-100'
        }`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <SafeIcon icon={FiMessageCircle} className="text-2xl" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="fixed bottom-6 right-6 z-50 w-96 h-[600px] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-primary-600 text-white rounded-t-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiBot} className="text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold">Joe</h3>
                  <p className="text-xs text-primary-100">Dad Support Resource Agent</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      message.type === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {message.type === 'bot' ? (
                      <MessageContent content={message.content} />
                    ) : (
                      <p className="text-sm">{message.content}</p>
                    )}
                    <p
                      className={`text-xs mt-2 ${
                        message.type === 'user' ? 'text-primary-100' : 'text-gray-500'
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-900 p-3 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about custody, support, or court procedures..."
                  className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  disabled={isTyping}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isTyping}
                  className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 text-white p-2 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiSend} className="text-lg" />
                </button>
              </div>

              {/* State Selection Pills */}
              {!selectedState && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Quick state selection:</p>
                  <div className="flex flex-wrap gap-1">
                    {['Florida', 'California', 'Texas', 'New York', 'Washington', 'Washington DC', 'Virginia', 'Vermont'].map((state) => (
                      <button
                        key={state}
                        onClick={() => {
                          setInputMessage(state);
                          handleSendMessage();
                        }}
                        className="text-xs bg-primary-100 hover:bg-primary-200 text-primary-700 px-2 py-1 rounded transition-colors"
                      >
                        {state}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;